// SD50 / FR-AD7 operator console: third-party integration credentials — Google and Microsoft
// OAuth clients (sign-in + calendar), Zoom and Teams notetaker apps, the Meet notetaker's Google
// session, hosted speech-to-text.
// GET /ops/integrations, PUT/DELETE /ops/integrations/{provider}. Same write-only contract as the
// SMTP card on /ops/email: secrets are never returned (only `secret_set`), a blank secret keeps
// the stored one, and the server applies a save on the next request — no redeploy.
//
// These live here, not in env vars or GitHub secrets (SD50): stored encrypted, changed only by a
// stepped-up platform admin, every change in the ops audit log. The notetaker bot's per-platform
// switches (SD51) and auto-leave rules (SD56) are settings, not credentials: /ops/notetaker.
import { useEffect, useState, type FormEvent } from "react";
import { Badge, Card, LinkField, ToggleRow } from "@grackle/ui";
import { Link } from "react-router-dom";
import { api, ApiFail } from "../api/client";
import type { IntegrationSource, ListResponse, OpsIntegrationOut, OpsIntegrationPut } from "../api/types";
import { Button, Field, Notice } from "../components/ui";
import {
  INTEGRATIONS,
  integrationMeta,
  validateIntegration,
  type IntegrationFormErrors,
  type IntegrationMeta,
} from "./integrationsForm";
import "../admin/admin.css";

const SOURCE_LABEL: Record<IntegrationSource, string> = {
  console: "Console",
  env: "Environment fallback",
  none: "Not configured",
};

function SourceBadge({ source }: { source: IntegrationSource }) {
  return (
    <Badge size="sm" tone={source === "console" ? "success" : source === "env" ? "accent" : "neutral"}>
      {SOURCE_LABEL[source]}
    </Badge>
  );
}

function fieldsFrom(meta: IntegrationMeta, cfg: OpsIntegrationOut): Record<string, string> {
  return Object.fromEntries(meta.fields.map((f) => [f.key, cfg.fields[f.key] ?? ""]));
}

export function IntegrationCard({ initial }: { initial: OpsIntegrationOut }) {
  const meta = integrationMeta(initial.provider);
  const [cfg, setCfg] = useState(initial);
  // A provider with no console row starts switched on: saving the card is how it is turned on.
  const [enabled, setEnabled] = useState(initial.source === "console" ? initial.enabled : true);
  const [fields, setFields] = useState<Record<string, string>>(() => fieldsFrom(meta, initial));
  const [secret, setSecret] = useState("");
  const [clearSecret, setClearSecret] = useState(false);
  const [errors, setErrors] = useState<IntegrationFormErrors>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apply = (next: OpsIntegrationOut) => {
    setCfg(next);
    setEnabled(next.source === "console" ? next.enabled : true);
    setFields(fieldsFrom(meta, next));
    setSecret("");
    setClearSecret(false);
    setErrors({});
  };

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setSaved(null);
    setError(null);
    const errs = validateIntegration(meta.provider, {
      enabled,
      fields,
      secret,
      clearSecret,
      secretSet: cfg.secret_set,
    });
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError("Fix the highlighted fields before saving.");
      return;
    }
    const body: OpsIntegrationPut = {
      enabled,
      fields: Object.fromEntries(Object.entries(fields).map(([k, v]) => [k, v.trim()])),
      secret: secret.trim() || undefined,
      clear_secret: clearSecret || undefined,
    };
    setSaving(true);
    try {
      const next = await api<OpsIntegrationOut>("PUT", `/ops/integrations/${meta.provider}`, body);
      apply(next);
      setSaved(
        next.configured
          ? `${meta.title} saved and in use. The secret is never shown again.`
          : `${meta.title} saved. It is switched off.`,
      );
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : `Couldn't save ${meta.title}.`);
    } finally {
      setSaving(false);
    }
  }

  async function onClear() {
    setSaving(true);
    setSaved(null);
    setError(null);
    try {
      const next = await api<OpsIntegrationOut>("DELETE", `/ops/integrations/${meta.provider}`);
      apply(next);
      setSaved(
        next.source === "env"
          ? `Console settings cleared. ${meta.title} is using the environment fallback.`
          : `Console settings cleared. ${meta.title} is not configured.`,
      );
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : `Couldn't clear ${meta.title}.`);
    } finally {
      setSaving(false);
    }
  }

  const secretHelp = cfg.secret_set
    ? "Leave blank to keep the stored secret. It is never shown after save."
    : "Stored encrypted. Never shown after save.";

  return (
    <Card size="sm" title={meta.title}>
      <div className="cluster" data-testid={`integration-status-${meta.provider}`}>
        <SourceBadge source={cfg.source} />
        {cfg.configured ? (
          <Badge tone="success" size="sm" icon="check-circle">
            In use
          </Badge>
        ) : (
          <Badge size="sm">Unavailable</Badge>
        )}
        {cfg.secret_set ? <Badge size="sm">Secret set</Badge> : null}
        {cfg.updated_at ? (
          <span className="gk-sm">
            Updated {new Date(cfg.updated_at).toLocaleString()}
            {cfg.updated_by_email ? ` by ${cfg.updated_by_email}` : ""}
          </span>
        ) : null}
      </div>
      <p className="gk-sm ops-card-note">{meta.summary}</p>
      {cfg.source === "env" ? (
        <Notice>
          Using a legacy environment value
          {cfg.env_fields && Object.values(cfg.env_fields).some(Boolean)
            ? ` (${meta.fields
                .map((f) => cfg.env_fields?.[f.key])
                .filter(Boolean)
                .join(", ")})`
            : ""}
          . Save the credentials here to take over; the environment value is read-only and is ignored once
          console settings exist.
        </Notice>
      ) : null}
      {cfg.source === "console" && !cfg.enabled ? (
        <Notice>Switched off here{cfg.env_fallback ? ", which also overrides the environment fallback" : ""}.</Notice>
      ) : null}

      <details className="ops-setup">
        <summary className="gk-sm">Setup notes</summary>
        <ol className="gk-sm">
          {meta.setup.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </details>

      {cfg.redirect_uris.length > 0 ? (
        <div className="ops-redirects">
          <div className="gk-sm">Redirect URIs to register with {meta.title}:</div>
          {cfg.redirect_uris.map((r) => (
            <div key={r.uri} className="ops-redirect">
              <span className="gk-caption">{r.label}</span>
              <LinkField url={r.uri} href={r.uri} size="sm" />
            </div>
          ))}
        </div>
      ) : null}

      <form onSubmit={onSave} className="ops-form" noValidate>
        <ToggleRow
          label="Use these settings"
          description={
            cfg.env_fallback
              ? "When off, this provider is unavailable — the environment fallback is not used while console settings exist."
              : "When off, this provider is unavailable."
          }
          checked={enabled}
          onChange={() => setEnabled((v) => !v)}
        />
        <div className="admin-two-col">
          {meta.fields.map((f) => (
            <Field
              key={f.key}
              label={f.required ? f.label : `${f.label} (optional)`}
              className="mono"
              value={fields[f.key] ?? ""}
              onChange={(e) => setFields((prev) => ({ ...prev, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              help={f.help}
              error={errors[f.key]}
              autoComplete="off"
              spellCheck={false}
            />
          ))}
          <Field
            label={meta.secretLabel}
            type="password"
            value={secret}
            onChange={(e) => {
              setSecret(e.target.value);
              setClearSecret(false);
            }}
            autoComplete="new-password"
            placeholder={cfg.secret_set ? "•••••••• (unchanged)" : ""}
            help={secretHelp}
            error={errors.secret}
          />
        </div>

        {cfg.secret_set ? (
          <label className="cluster gk-sm">
            <input
              type="checkbox"
              checked={clearSecret}
              onChange={(e) => {
                setClearSecret(e.target.checked);
                if (e.target.checked) setSecret("");
              }}
            />
            Clear the stored secret
          </label>
        ) : null}

        {error ? <Notice tone="error">{error}</Notice> : null}
        {saved ? <Notice>{saved}</Notice> : null}

        <div className="cluster">
          <Button type="submit" loading={saving}>
            Save {meta.title}
          </Button>
          {cfg.source === "console" ? (
            <Button type="button" variant="ghost" disabled={saving} onClick={onClear}>
              Clear console settings
            </Button>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

export default function OpsIntegrationsPage() {
  const [items, setItems] = useState<OpsIntegrationOut[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    api<ListResponse<OpsIntegrationOut>>("GET", "/ops/integrations")
      .then((res) => setItems(res.items))
      .catch((err) => setLoadError(err instanceof ApiFail ? err.message : "Couldn't load integrations."));
  }, []);

  const ordered = items
    ? INTEGRATIONS.map((m) => items.find((i) => i.provider === m.provider)).filter(
        (i): i is OpsIntegrationOut => i !== undefined,
      )
    : [];

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Integrations</h1>
          <p>
            Credentials Grackle uses to reach other services: Google and Microsoft for sign-in and calendars, Zoom
            and Teams for the notetaker's meeting lookups, the Meet notetaker's Google session, and hosted
            speech-to-text. Each is stored encrypted and write-only (you can replace or clear it, never read it
            back) and applies without a redeploy. The notetaker's on/off switches and auto-leave rules are on{" "}
            <Link to="/ops/notetaker">Notetaker</Link>.
          </p>
        </div>
      </header>
      {loadError ? <Notice tone="error">{loadError}</Notice> : null}
      {!items && !loadError ? <div className="gk-sm">Loading integrations…</div> : null}
      {ordered.map((item) => (
        <IntegrationCard key={item.provider} initial={item} />
      ))}
    </>
  );
}
